import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
} from '@mui/material';

import {
  AgencyQuickReplyDto,
  CreateAgencyQuickReplyDto,
} from '@bandido/shared/dtos';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';

type WhatsappQuickReplyDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateAgencyQuickReplyDto) => Promise<void>;
  quickReply?: AgencyQuickReplyDto;
  isSubmitting?: boolean;
};

const defaultValues: CreateAgencyQuickReplyDto = {
  slug: '',
  name: '',
  body: '',
  enabled: true,
};

const quickReplySlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const WhatsappQuickReplyDialog = ({
  open,
  onClose,
  onSubmit,
  quickReply,
  isSubmitting = false,
}: WhatsappQuickReplyDialogProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit, reset } = useForm<CreateAgencyQuickReplyDto>({
    defaultValues,
  });

  useEffect(() => {
    if (!open) return;

    reset(
      quickReply
        ? {
            slug: quickReply.slug,
            name: quickReply.name,
            body: quickReply.body,
            enabled: quickReply.enabled,
          }
        : defaultValues,
    );
  }, [open, quickReply, reset]);

  const handleFormSubmit = async (values: CreateAgencyQuickReplyDto) => {
    await onSubmit({
      slug: values.slug.trim(),
      name: values.name.trim(),
      body: values.body,
      enabled: values.enabled,
    });
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {quickReply
          ? t('whatsapp.settings.quickReplies.form.editTitle')
          : t('whatsapp.settings.quickReplies.form.createTitle')}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <Controller
            name="slug"
            control={control}
            rules={{
              required: t('common.mandatoryField'),
              maxLength: {
                value: 64,
                message: t('whatsapp.settings.quickReplies.form.slugInvalid'),
              },
              pattern: {
                value: quickReplySlugPattern,
                message: t('whatsapp.settings.quickReplies.form.slugInvalid'),
              },
              validate: (value) =>
                value.trim() === value ||
                t('whatsapp.settings.quickReplies.form.slugInvalid'),
            }}
            render={({ field, fieldState: { error } }) => (
              <FormControl error={Boolean(error)} fullWidth>
                <TextField
                  {...field}
                  label={t('whatsapp.settings.quickReplies.form.slug')}
                  placeholder={t('whatsapp.settings.quickReplies.form.slugPlaceholder')}
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 64 }}
                  error={Boolean(error)}
                  helperText={
                    !error ? t('whatsapp.settings.quickReplies.form.slugHelper') : undefined
                  }
                />
                {error?.message && (
                  <FormHelperText>{error.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          <ControlledTextField
            control={control}
            name="name"
            label={t('whatsapp.settings.quickReplies.form.name')}
            placeholder={t('whatsapp.settings.quickReplies.form.namePlaceholder')}
            required
            errorMessage={t('common.form.required')}
            inputProps={{ maxLength: 100 }}
            fullWidth
          />

          <ControlledTextField
            control={control}
            name="body"
            label={t('whatsapp.settings.quickReplies.form.body')}
            placeholder={t('whatsapp.settings.quickReplies.form.bodyPlaceholder')}
            helperText={t('whatsapp.settings.quickReplies.form.bodyHelper')}
            required
            errorMessage={t('common.form.required')}
            inputProps={{ maxLength: 2000 }}
            multiline
            minRows={5}
            fullWidth
          />

          <Controller
            name="enabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                }
                label={t('whatsapp.settings.quickReplies.form.enabled')}
              />
            )}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <LoadingButton loading={isSubmitting} type="submit" variant="contained">
            {t('common.save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
